<template>
  <div class="rm rm-theme">
    <!-- Početna: izbor izveštaja -->
    <template v-if="!currentScreen">
      <div class="rm-home-header">
        <span v-if="companyName" class="rm-muted">{{ companyName }}</span>
        <h1 class="rm-home-header__title">{{ $t("report_mobile.title") }}</h1>
      </div>

      <div class="rm-chip-row rm-home-periods">
        <button
          v-for="preset in quickPresets"
          :key="preset.key"
          type="button"
          class="rm-chip rm-chip--lg"
          :class="{ 'rm-chip--active': activePresetKey === preset.key }"
          :aria-pressed="String(activePresetKey === preset.key)"
          @click="applyPeriod(preset)"
        >
          {{ presetChipLabel(preset) }}
        </button>
        <button
          v-if="!activeQuickPreset"
          type="button"
          class="rm-chip rm-chip--lg rm-chip--active rm-num"
          aria-pressed="true"
          @click="periodSheet = true"
        >
          {{ periodShort }}
        </button>
        <button
          type="button"
          class="rm-chip rm-chip--lg rm-chip--icon"
          :aria-label="$t('report_mobile.choose_period')"
          @click="periodSheet = true"
        >
          <v-icon size="20" color="#1c1917">mdi-calendar-blank-outline</v-icon>
        </button>
      </div>

      <button
        v-if="showCompanies"
        type="button"
        class="rm-card cm-companies-btn"
        @click="filterSheet = true"
      >
        <v-icon size="20" color="#c2410c">mdi-domain</v-icon>
        <span class="cm-companies-btn__text">
          <span class="rm-muted">{{ isMulti ? $t("restaurants") : $t("companies") }}</span>
          <span class="cm-companies-btn__name">{{ companiesLabel }}</span>
        </span>
        <v-icon size="18" color="#8a817a">mdi-chevron-down</v-icon>
      </button>

      <div class="rm-home-groups">
        <section v-for="group in menuGroups" :key="group.key" class="rm-home-group">
          <h2 class="rm-section-title">{{ group.title }}</h2>
          <div class="rm-card">
            <button
              v-for="item in group.items"
              :key="item.key"
              type="button"
              class="rm-menu-row"
              @click="openScreen(item.key)"
            >
              <span class="rm-menu-row__icon">
                <v-icon size="20" color="#c2410c">{{ item.icon }}</v-icon>
              </span>
              <span class="rm-menu-row__text">
                <span class="rm-menu-row__title">{{ item.title }}</span>
                <span class="rm-muted">{{ item.subtitle }}</span>
              </span>
              <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
            </button>
          </div>
        </section>
      </div>
    </template>

    <!-- Pojedinačni izveštaj -->
    <template v-else>
      <div class="rm-detail-top">
        <div class="rm-detail-header">
          <button
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('report_mobile.back')"
            @click="closeScreen"
          >
            <v-icon size="24" color="#1c1917">mdi-chevron-left</v-icon>
          </button>
          <div class="rm-detail-header__text">
            <h1 class="rm-detail-header__title">{{ currentScreen.title }}</h1>
            <span class="rm-muted rm-detail-header__sub">{{ headerSubtitle }}</span>
          </div>
          <button
            v-if="currentScreen.headerSearch"
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('search')"
            :aria-pressed="String(searchOpen)"
            @click="toggleSearch"
          >
            <v-icon size="22" color="#1c1917">mdi-magnify</v-icon>
          </button>
          <button
            v-if="!currentScreen.noExport"
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('report_mobile.share')"
            @click="actionsSheet = true"
          >
            <v-icon size="22" color="#1c1917">mdi-export-variant</v-icon>
          </button>
          <button
            v-else-if="currentScreen.header !== 'worker' && currentScreen.header !== 'form'"
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('report_mobile.refresh')"
            @click="runAction('reload')"
          >
            <v-icon size="22" color="#1c1917">mdi-refresh</v-icon>
          </button>
        </div>

        <div v-if="currentScreen.headerSearch && searchOpen" class="rm-detail-search">
          <label class="rm-search">
            <v-icon size="18" color="#5f5852">mdi-magnify</v-icon>
            <span class="rm-visually-hidden">{{ $t("search") }}</span>
            <input ref="searchInput" v-model="search" type="search" :placeholder="$t('search')" />
          </label>
        </div>

        <div v-if="!currentScreen.header" class="rm-chip-row">
          <button type="button" class="rm-chip" @click="periodSheet = true">
            <span class="rm-num">{{ periodShort }}</span>
            <v-icon size="16" color="#5f5852">mdi-chevron-down</v-icon>
          </button>
          <button
            v-if="showCompanies && !currentScreen.noCompanies"
            type="button"
            class="rm-chip"
            :class="{ 'rm-chip--accent': !allCompaniesSelected }"
            @click="filterSheet = true"
          >
            {{ companiesLabel }}
            <v-icon size="16" :color="allCompaniesSelected ? '#5f5852' : '#9a3412'">mdi-chevron-down</v-icon>
          </button>
          <button
            v-if="currentScreen.shifts"
            type="button"
            class="rm-chip"
            :class="{ 'rm-chip--accent': !allShiftsSelected }"
            @click="filterSheet = true"
          >
            {{ shiftsLabel }}
            <v-icon size="16" :color="allShiftsSelected ? '#5f5852' : '#9a3412'">mdi-chevron-down</v-icon>
          </button>
        </div>
      </div>

      <div v-if="branchesLoading" class="rm-empty">
        <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
      </div>
      <div v-else-if="!ready" class="rm-empty">
        <v-icon size="40" color="#8a817a">mdi-domain</v-icon>
        {{ branchesFailed ? $t("report_mobile.load_failed") : $t("no_companies_selected") }}
        <button v-if="branchesFailed" type="button" class="rm-btn rm-btn--sm cm-retry" @click="loadBranches">
          {{ $t("report_mobile.retry") }}
        </button>
      </div>
      <component
        :is="currentScreen.component"
        v-else
        :key="`${currentScreenKey}-${reloadKey}`"
        v-bind="screenProps"
        @open-screen="openScreen"
      ></component>
    </template>

    <report-mobile-period-sheet
      v-model="periodSheet"
      :from="from"
      :to="to"
      :presets="presets"
      @apply="applyPeriod"
    ></report-mobile-period-sheet>

    <report-mobile-filter-sheet
      v-model="filterSheet"
      :from="from"
      :to="to"
      :presets="presets"
      :shifts="shifts"
      :show-shifts="Boolean(currentScreen && currentScreen.shifts)"
      :show-companies="showCompanies && !(currentScreen && currentScreen.noCompanies)"
      :company-labels="sheetCompanyLabels"
      company-mode="multi"
      :companies="branches"
      :selected-company-ids="selectedCompanyIds"
      :views="[]"
      @open-period="openPeriodFromFilters"
      @apply="applyFilters"
    ></report-mobile-filter-sheet>

    <ui-sheet v-model="actionsSheet" :label="$t('report_mobile.more_options')">
      <ul class="rm-list rm-actions">
        <li>
          <button type="button" class="rm-actions__row" @click="runAction('print')">
            <v-icon size="22" color="#1c1917">mdi-printer-outline</v-icon>
            {{ $t("report_mobile.print_pdf") }}
          </button>
        </li>
        <li v-if="!(currentScreen && currentScreen.noExcel)">
          <button type="button" class="rm-actions__row" @click="runAction('excel')">
            <v-icon size="22" color="#15803d">mdi-microsoft-excel</v-icon>
            {{ $t("report_mobile.download_excel") }}
          </button>
        </li>
        <li>
          <button type="button" class="rm-actions__row" @click="runAction('reload')">
            <v-icon size="22" color="#1c1917">mdi-refresh</v-icon>
            {{ $t("report_mobile.refresh") }}
          </button>
        </li>
      </ul>
    </ui-sheet>
  </div>
</template>

<script>
import ReportMobilePeriodSheet from "@/components/report/mobile/ReportMobilePeriodSheet.vue";
import ReportMobileFilterSheet from "@/components/report/mobile/ReportMobileFilterSheet.vue";
import "@/components/report/mobile/reportMobileShell.scss";
import "@/components/report/mobile/screens/group/reportMobileGroup.scss";
import "./screens/multi/companyMobileMulti.scss";
import { buildPeriodPresets, findPresetKey, formatRange, formatShortDate } from "@/components/report/shared/periods";
import { QUERY_KEYS } from "@/components/report/desktop/reportQuery";
import hasCompanyReportState from "../hasCompanyReportState";
import hasCompanyGroupUsers from "../hasCompanyGroupUsers";
import { companyVariant } from "../companyReportMenu";
import { EMPLOYEE_SCREEN, buildCompanyMenu, buildCompanyScreens } from "../companyReportScreens";

/**
 * Izveštaji firme na telefonu: početna sa izborom izveštaja, pa izveštaj sa zaglavljem, periodom, firmama i
 * smenama. Isti stil i ponašanje kao mobilni /izvestaj. Period, smene, firme i pretraga (stanje zajedničko sa
 * računarom) su u hasCompanyReportState; ovde su izbor izveštaja i raspored za telefon.
 */
export default {
  name: "CompanyReportMobile",
  mixins: [hasCompanyReportState, hasCompanyGroupUsers],
  components: { ReportMobilePeriodSheet, ReportMobileFilterSheet },
  data: () => ({
    searchOpen: false,
    pendingSearch: "",
    periodSheet: false,
    filterSheet: false,
    actionsSheet: false,
    reloadKey: 0,
    pushedScreens: 0,
    /** Korisnik izabran u "Kreditnim korisnicima" za "Dodaj kredit" (firma "više restorana"). */
    topupUserId: null,
  }),
  computed: {
    presets() {
      return buildPeriodPresets();
    },
    quickPresets() {
      return this.presets.filter((preset) => preset.key !== "last_month");
    },
    activePresetKey() {
      return findPresetKey(this.presets, this.from, this.to);
    },
    activeQuickPreset() {
      return this.quickPresets.some((preset) => preset.key === this.activePresetKey);
    },
    periodShort() {
      if (this.activePresetKey) {
        return this.$t(`report_mobile.presets.${this.activePresetKey}`);
      }
      return formatRange(this.from, this.to);
    },
    periodLong() {
      if (this.from !== this.to) {
        return formatRange(this.from, this.to);
      }
      const weekday = this.localeDate(this.from, { weekday: "long" });
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${formatShortDate(this.from, true)}`;
    },
    employeeContext() {
      return this.$store.state.report.selectedEmployee;
    },
    /** Obična firma, firma tipa "grupa", firma "više restorana" ili stranica "Izveštaj kantina": svaka ima svoj skup izveštaja. */
    variant() {
      return this.canteen ? "canteen" : companyVariant(this.$store.state.company);
    },
    screens() {
      return buildCompanyScreens((key, params) => this.$t(key, params), this.employeeContext, this.variant);
    },
    menuGroups() {
      return buildCompanyMenu((key, params) => this.$t(key, params), this.variant).map((group) => ({
        ...group,
        items: group.items.map((key) => ({ key, ...this.screens[key] })),
      }));
    },
    currentScreenKey() {
      const key = this.$route.query[QUERY_KEYS.report];
      if (key === EMPLOYEE_SCREEN && this.employeeContext) {
        return key;
      }
      // Mesečni pregled radnika nema smisla bez izabranog radnika (npr. posle osvežavanja stranice).
      const wanted = key === EMPLOYEE_SCREEN ? (this.variant === "group" ? "groupUsers" : "employees") : key;
      const allowed = this.menuGroups.some((group) => group.items.some((item) => item.key === wanted));
      return allowed ? wanted : null;
    },
    currentScreen() {
      return this.currentScreenKey ? this.screens[this.currentScreenKey] : null;
    },
    /** Radnike za izveštaj "Pregled korisnika" grupne firme učitava ljuska (hasCompanyGroupUsers). */
    feedsGroupUsers() {
      return Boolean(this.currentScreen && this.currentScreen.usersFeed);
    },
    /** Šta ekran dobija od ljuske: smene (kad ih koristi) i radnike (kad mu ih ljuska učitava). */
    screenProps() {
      const screen = this.currentScreen;
      return {
        ...(screen && screen.shifts ? { shifts: this.shifts } : {}),
        ...(screen && screen.userPreselect ? { preselectedUserId: this.topupUserId } : {}),
        ...(this.feedsGroupUsers ? this.groupUsersProps : {}),
      };
    },
    /** Nazivi u izboru stavki: kod firme "više restorana" su stavke restorani. */
    sheetCompanyLabels() {
      return this.isMulti ? { title: this.$t("restaurants"), search: this.$t("guest_desktop.search_restaurants") } : null;
    },
    shiftsLabel() {
      if (this.allShiftsSelected) {
        return this.$t("report_mobile.all_shifts");
      }
      if (this.shifts.length === 1) {
        return this.$t("report_mobile.shift_n", { n: this.shifts[0] });
      }
      return this.$t("report_mobile.shifts_list", { list: this.shifts.join(", ") });
    },
    headerSubtitle() {
      const screen = this.currentScreen;
      if (!screen) {
        return "";
      }
      if (screen.hint) {
        return screen.hint;
      }
      if (screen.header === "worker") {
        const employee = this.employeeContext;
        return employee ? [`ID ${employee.id}`, employee.companyName].filter(Boolean).join(" · ") : "";
      }
      return this.periodLong;
    },
    urlQuery() {
      const query = { ...this.stateQuery };
      if (this.currentScreenKey) {
        query[QUERY_KEYS.report] = this.currentScreenKey;
      }
      return query;
    },
  },
  watch: {
    urlQuery: {
      handler(query) {
        this.writeUrl(query);
      },
      deep: true,
    },
    currentScreenKey(newKey, oldKey) {
      if (newKey === oldKey) {
        return;
      }
      this.search = this.pendingSearch;
      this.pendingSearch = "";
      this.searchOpen = Boolean(this.search) && Boolean(this.currentScreen?.headerSearch);
      window.scrollTo(0, 0);
    },
  },
  mounted() {
    this.writeUrl(this.urlQuery);
  },
  methods: {
    presetChipLabel(preset) {
      const label = this.$t(`report_mobile.presets.${preset.key}`);
      if (this.activePresetKey === preset.key && preset.from === preset.to) {
        return `${label} · ${formatShortDate(preset.from)}`;
      }
      return label;
    },
    applyPeriod(range) {
      this.setPeriod(range);
    },
    openPeriodFromFilters() {
      this.filterSheet = false;
      this.periodSheet = true;
    },
    applyFilters({ from, to, shifts, companyIds }) {
      if (from !== this.from || to !== this.to) {
        this.applyPeriod({ from, to });
      }
      this.shifts = shifts;
      this.setCompanies(companyIds);
    },
    openScreen(key, { search = "", userId = null } = {}) {
      if (this.screens[key] && this.screens[key].userPreselect) {
        this.topupUserId = userId;
      }
      if (key === this.currentScreenKey) {
        this.search = search;
        return;
      }
      this.pendingSearch = search;
      this.pushedScreens += 1;
      this.$router.push({ query: { ...this.$route.query, [QUERY_KEYS.report]: key } }).catch(() => {});
    },
    closeScreen() {
      // Ekran otvoren sa ove stranice → nazad kroz istoriju (radi i hardverski "nazad");
      // direktan link (?r=...) → zameni adresu, da ne izađemo sa /company/report.
      if (this.pushedScreens > 0) {
        this.pushedScreens -= 1;
        this.$router.back();
        return;
      }
      const query = { ...this.$route.query };
      delete query[QUERY_KEYS.report];
      this.$router.replace({ query }).catch(() => {});
    },
    toggleSearch() {
      this.searchOpen = !this.searchOpen;
      if (this.searchOpen) {
        this.$nextTick(() => this.$refs.searchInput?.focus());
      } else {
        this.search = "";
      }
    },
    runAction(action) {
      this.actionsSheet = false;
      if (action === "print") {
        this.$store.commit("SET_PRINTING");
      } else if (action === "excel") {
        this.$store.commit("SET_EXCEL");
      } else if (action === "reload") {
        this.reloadKey += 1;
        if (this.feedsGroupUsers) {
          this.loadGroupUsers();
        }
      }
    },
  },
};
</script>

<style lang="scss">
.rm-theme .cm-companies-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: calc(100% - 32px);
  margin: 0 16px 8px;
  min-height: 60px;
  padding: 10px 14px;
  text-align: left;
  color: var(--rm-text);

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__name {
    font-size: 16px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }
}

.rm-theme .cm-retry {
  width: auto;
  padding: 0 20px;
}
</style>
